import React, { ReactElement, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { makeStyles } from '@mui/styles';
import {
	FormControlLabel,
	IconButton,
	Pagination,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
} from '@mui/material';
import { colors, theme } from '../../theme';
import { CUMasterDataType, CUProductTypeMappingType, CUProductTypeRequestType } from '../../types/cu.types';
import { PostResponse } from '../../types/common.types';
import { getProductTypeMapping, saveProductTypeMapping } from '../../services/cu-service';
import { msalInstance } from '../../config/msalConfig';
import { useAsyncOperation } from '../../utils/use-async-operation';
import { CustomAlert } from '../shared/alert/alert';
import { CustomButton } from '../shared/button/button';
import { Loader } from '../shared/loader/loader';
import { MultiSelectDropDown } from '../shared/dropdown/multiSelect';
import { ToolTip } from '../shared/tooltip/tooltip';
import { Search } from '../shared/search/search';
import { SingleSelectDropDown } from '../shared/dropdown/singleSelect';
import { TextBox } from '../shared/textbox/textbox';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

export const testid = 'product-type';

const color = colors();
const themes = theme().typography;
export const ProductType = ({ master }: CUMasterDataType): ReactElement => {
	const classes = useStyles();
	const isAdmin: boolean = localStorage.getItem('roles')?.includes('TT.BrandSCAdmin') || false;
	const productCategoryList = master.productCategory.map((item) => ({
		...item,
		name: item.name.replace('__', ' - '),
	}));
	const productDetailList = master.productDetail.map((item) => ({
		...item,
		name: item.name.replace('__', ' - '),
	}));
	const userAccount = msalInstance.getActiveAccount();
	const loggedInUser = userAccount ? `${userAccount.name?.split('(')[0]}| ${userAccount.username}` : '';

	const [initialData, setInitialData] = useState<CUProductTypeMappingType[]>([]);
	const [productTypeData, setProductTypeData] = useState<CUProductTypeMappingType[]>([]);
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState<number>(25);
	const [pageCount, setPageCount] = useState<number>(1);
	const [openErrorAlert, setOpenErrorAlert] = useState(false);
	const [openSaveAlert, setOpenSaveAlert] = useState(false);
	const [showBlanks, setShowBlanks] = useState(false);
	const [productDetail, setProductDetail] = useState<string[]>([]);
	const [productCategory, setProductCategory] = useState<string[]>([]);
	const [searchText, setSearchText] = useState<string>();
	const [rowData, setRowData] = useState<CUProductTypeMappingType>();
	const [disableActions, setDisableActions] = useState<boolean>(false);
	const [detail1, setDetail1] = useState<string>();
	const [detail2, setDetail2] = useState<string>();
	const [detail3, setDetail3] = useState<string>();
	const [detail4, setDetail4] = useState<string>();
	const [category1, setCategory1] = useState<string>();
	const [category2, setCategory2] = useState<string>();
	const [category3, setCategory3] = useState<string>();
	const [category4, setCategory4] = useState<string>();
	const [comment, setComment] = useState<string>();
	const initialLoadTriggered = useRef(false);

	const [getProductTypes, productTypeBusy, productType, productTypeError] =
		useAsyncOperation<CUProductTypeMappingType[]>(getProductTypeMapping);
	const [saveProductType, saveBusy, saveData, saveError] = useAsyncOperation<PostResponse>(saveProductTypeMapping);

	useEffect(() => {
		if (initialLoadTriggered.current) return;
		initialLoadTriggered.current = true;
		getProductTypes();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (productTypeError) setOpenErrorAlert(true);
	}, [productTypeError]);

	useEffect(() => {
		if (saveData) {
			getProductTypes();
			setOpenSaveAlert(true);
			setDisableActions(false);
		}
	}, [saveData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (saveError) setOpenSaveAlert(true);
	}, [saveError]);

	useEffect(() => {
		if (productType) {
			setShowBlanks(false);
			onClearClick();
			const sortedData: CUProductTypeMappingType[] = [...productType].sort(
				(a, b) => new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime()
			);
			setInitialData(sortedData);
			setProductTypeData(sortedData);
			setPageCount(Math.floor(sortedData.length / rowsPerPage));
		}
	}, [productType]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (productTypeData) setPageCount(Math.floor(productTypeData.length / rowsPerPage));
	}, [rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		onClearClick();
	}, [showBlanks]); // eslint-disable-line react-hooks/exhaustive-deps

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	};

	const handleRowPerPageChange = (value: string) => {
		setRowsPerPage(parseInt(value));
		setPage(1);
	};

	const onClearClick = () => {
		setPage(1);
		if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
			try {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			} catch {
				window.scrollTo(0, 0);
			}
		}
		setProductDetail([]);
		setProductCategory([]);
		setSearchText(undefined);

		if (showBlanks && initialData) {
			let blankData = initialData.filter((d) => d.cuProductDetail === null || d.cuProductDetail.length === 0);
			setPageCount(Math.floor(blankData.length / rowsPerPage));
			setProductTypeData(blankData);
		} else {
			setProductTypeData(initialData || []);
			!showBlanks && setPageCount(Math.floor(initialData.length / rowsPerPage));
		}
	};

	const onSearchClick = () => {
		if (productDetail.length !== 0 || productCategory.length !== 0 || searchText) {
			let filteredData = initialData || [];
			setPage(1);

			if (productDetail.length > 0 && filteredData.length > 0) {
				var prodDetail = productDetail.map((pd) => pd.substring(0, pd.indexOf(' - ')));
				filteredData = filteredData.filter((d) =>
					d.cuProductDetail?.some((det) => prodDetail.some((code) => det.startsWith(code)))
				);
			}
			if (productCategory.length > 0 && filteredData.length > 0) {
				var prodCat = productCategory.map((pc) => pc.substring(0, pc.indexOf(' - ')));
				filteredData = filteredData.filter((d) =>
					d.cuProductCategory?.some((cat) => prodCat.some((code) => cat.startsWith(code)))
				);
			}
			if (searchText && filteredData.length > 0) {
				filteredData = filteredData.filter(
					(d) =>
						d.productTypeName.toLowerCase().includes(searchText.toLowerCase()) ||
						d.productTypeId.includes(searchText) ||
						d.comments?.toLowerCase().includes(searchText.toLowerCase())
				);
				if (showBlanks)
					filteredData = filteredData.filter((d) => d.cuProductDetail === null || d.cuProductDetail.length === 0);
			}

			setProductTypeData(filteredData);
			setPageCount(Math.floor(filteredData.length / rowsPerPage));
		}
	};

	const handleAddEditClick = (row: CUProductTypeMappingType, isEdit: boolean) => {
		let data = [...productTypeData];
		data.filter((d) => d.productTypeId === row.productTypeId)[0].isModify = true;
		if (isEdit) {
			data.filter((d) => d.productTypeId === row.productTypeId)[0].isEdit = true;
			setDetail1(
				productDetailList.filter(
					(pd) =>
						pd.name.substring(0, pd.name.indexOf(' - ')) ===
						row.cuProductDetail[0].substring(0, row.cuProductDetail[0].indexOf('__'))
				)[0]?.name || undefined
			);
			setDetail2(
				productDetailList.filter(
					(pd) =>
						pd.name.substring(0, pd.name.indexOf(' - ')) ===
						row.cuProductDetail[1]?.substring(0, row.cuProductDetail[0].indexOf('__'))
				)[0]?.name || undefined
			);
			setDetail3(
				productDetailList.filter(
					(pd) =>
						pd.name.substring(0, pd.name.indexOf(' - ')) ===
						row.cuProductDetail[2]?.substring(0, row.cuProductDetail[0].indexOf('__'))
				)[0]?.name || undefined
			);
			setDetail4(
				productDetailList.filter(
					(pd) =>
						pd.name.substring(0, pd.name.indexOf(' - ')) ===
						row.cuProductDetail[3]?.substring(0, row.cuProductDetail[0].indexOf('__'))
				)[0]?.name || undefined
			);
			setCategory1(
				productCategoryList.filter(
					(pc) =>
						pc.name.substring(0, pc.name.indexOf(' - ')) ===
						row.cuProductCategory[0].substring(0, row.cuProductCategory[0].indexOf('__'))
				)[0]?.name || undefined
			);
			setCategory2(
				productCategoryList.filter(
					(pc) =>
						pc.name.substring(0, pc.name.indexOf(' - ')) ===
						row.cuProductCategory[1]?.substring(0, row.cuProductCategory[0].indexOf('__'))
				)[0]?.name || undefined
			);
			setCategory3(
				productCategoryList.filter(
					(pc) =>
						pc.name.substring(0, pc.name.indexOf(' - ')) ===
						row.cuProductCategory[2]?.substring(0, row.cuProductCategory[0].indexOf('__'))
				)[0]?.name || undefined
			);
			setCategory4(
				productCategoryList.filter(
					(pc) =>
						pc.name.substring(0, pc.name.indexOf(' - ')) ===
						row.cuProductCategory[3]?.substring(0, row.cuProductCategory[0].indexOf('__'))
				)[0]?.name || undefined
			);
			setComment(row.comments);
		}
		setProductTypeData(data);
		setRowData(row);
		setDisableActions(true);
	};

	const onDiscardClick = (id: string) => {
		let data = [...productTypeData];
		data.filter((d) => d.productTypeId === id)[0].isModify = false;
		data.filter((d) => d.productTypeId === id)[0].isEdit = false;
		setProductTypeData(data);
		setDisableActions(false);
		setDetail1(undefined);
		setDetail2(undefined);
		setDetail3(undefined);
		setDetail4(undefined);
		setCategory1(undefined);
		setCategory2(undefined);
		setCategory3(undefined);
		setCategory4(undefined);
		setComment(undefined);
	};

	const onSaveClick = () => {
		if (rowData && detail1 && category1) {
			const detail = [
				detail1.replace(' - ', '__'),
				detail2 ? detail2.replace(' - ', '__') : '',
				detail3 ? detail3.replace(' - ', '__') : '',
				detail4 ? detail4.replace(' - ', '__') : '',
			].filter((d) => d.length > 0);
			const category = [
				category1.replace(' - ', '__'),
				category2 ? category2.replace(' - ', '__') : '',
				category3 ? category3.replace(' - ', '__') : '',
				category4 ? category4.replace(' - ', '__') : '',
			].filter((c) => c.length > 0);

			const requestData: CUProductTypeRequestType = {
				id: rowData.isEdit ? rowData.id : null,
				productTypeId: rowData.productTypeId,
				productTypeName: rowData.productTypeName,
				cuProductDetail: detail,
				cuProductCategory: category,
				comments: comment || '',
				createdDate: rowData.isEdit ? rowData.createdDate : null,
				updatedBy: loggedInUser,
			};
			saveProductType(requestData);
		}
	};

	return (
		<>
			<Loader key={`${testid}-loader`} open={productTypeBusy || saveBusy} message="Loading..." />
			{openErrorAlert && (
				<CustomAlert
					open={openErrorAlert}
					message="Something went wrong while retrieving product type mapping data!"
					severity="error"
					timeout={5000}
					isFade={false}
					onCloseCallback={() => {
						setOpenErrorAlert(false);
					}}
				/>
			)}
			{openSaveAlert && (
				<div className={classes.alert}>
					<CustomAlert
						open={openSaveAlert}
						timeout={7000}
						message={saveData && !saveError ? 'Mapping saved successfully!' : 'Something went wrong!'}
						severity={saveData && !saveError ? 'success' : 'error'}
						onCloseCallback={() => {
							setOpenSaveAlert(false);
						}}
					/>
				</div>
			)}
			<div className={classes.filters}>
				{!showBlanks && (
					<MultiSelectDropDown
						label="Product Detail"
						width={185}
						data={productDetailList.map((list) => list.name)}
						value={productDetail}
						hasSearch
						onSelectCallback={setProductDetail}
					/>
				)}
				{!showBlanks && (
					<MultiSelectDropDown
						label="Product Category"
						width={185}
						data={productCategoryList.map((list) => list.name)}
						value={productCategory}
						hasSearch
						onSelectCallback={setProductCategory}
					/>
				)}
				<Search placeholder="H&M Type ID, Name, Comments" value={searchText || ''} onSearchCallback={setSearchText} />
				<CustomButton id="search" text="Search" width={90} variant="outlined" onClickCallback={onSearchClick} />
				{(productDetail.length > 0 || productCategory.length > 0 || searchText !== undefined) && (
					<CustomButton id="clear" text="Clear" width={90} onClickCallback={onClearClick} />
				)}
				<FormControlLabel
					className={classes.label}
					control={
						<Switch
							checked={showBlanks}
							onClick={() => {
								setShowBlanks(!showBlanks);
								setPage(1);
							}}
							data-testid={`${testid}-blanks`}
						/>
					}
					label="Only Blanks"
					labelPlacement="start"
				/>
			</div>

			{productTypeData && (
				<>
					<TableContainer className={classes.table} data-testid={`${testid}-table`}>
						<Table stickyHeader>
							<TableHead>
								<TableRow key={`${testid}-header`}>
									<TableCell width="9%">Product Type ID</TableCell>
									<TableCell width="14%">Product Type Name</TableCell>
									<TableCell width="20%">TE Product Detail</TableCell>
									<TableCell width="20%">TE Product Category</TableCell>
									<TableCell width="10%">Mapped Date</TableCell>
									<TableCell width="16%">Comments</TableCell>
									{isAdmin && <TableCell width="1%" />}
								</TableRow>
							</TableHead>
							<TableBody data-testid={`${testid}-body`}>
								{(rowsPerPage > 0
									? productTypeData.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage)
									: productTypeData
								).map((row, i) => (
									<TableRow key={i} hover={row.isModify ? false : true}>
										{row.isModify ? (
											<>
												<TableCell className={classes.modifyRow}>{row.productTypeId}</TableCell>
												<TableCell>{row.productTypeName}</TableCell>
												<TableCell
													sx={{
														textWrap: 'nowrap',
														maxWidth: '100px',
													}}
												>
													<div>
														<div className={classes.divComp}>
															<SingleSelectDropDown
																labelText="Product Category 1"
																label=""
																data={productCategoryList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={category1 || ''}
																width={240}
																hasSearch
																onSelectCallback={setCategory1}
															/>
															<SingleSelectDropDown
																labelText="Product Category 2"
																label=""
																data={productCategoryList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={category2 || ''}
																width={240}
																hasSearch
																onSelectCallback={setCategory2}
															/>
															<SingleSelectDropDown
																labelText="Product Category 3"
																label=""
																data={productCategoryList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={category3 || ''}
																width={240}
																hasSearch
																onSelectCallback={setCategory3}
															/>
															<SingleSelectDropDown
																labelText="Product Category 4"
																label=""
																data={productCategoryList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={category4 || ''}
																width={240}
																hasSearch
																onSelectCallback={setCategory4}
															/>
														</div>
														<div className={classes.divComp}>
															<SingleSelectDropDown
																labelText="Product Detail 1"
																label=""
																data={productDetailList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={detail1 || ''}
																width={240}
																hasSearch
																onSelectCallback={setDetail1}
															/>
															<SingleSelectDropDown
																labelText="Product Detail 2"
																label=""
																data={productDetailList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={detail2 || ''}
																width={240}
																hasSearch
																onSelectCallback={setDetail2}
															/>
															<SingleSelectDropDown
																labelText="Product Detail 3"
																label=""
																data={productDetailList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={detail3 || ''}
																width={240}
																hasSearch
																onSelectCallback={setDetail3}
															/>
															<SingleSelectDropDown
																labelText="Product Detail 4"
																label=""
																data={productDetailList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={detail4 || ''}
																width={240}
																hasSearch
																onSelectCallback={setDetail4}
															/>
														</div>
														<TextBox
															id={`${testid}-comment`}
															label=""
															labelText="Comments"
															width={240}
															value={comment || ''}
															onChangeCallback={setComment}
														/>
													</div>
													<div className={classes.actions}>
														{!(detail1 && category1) && (
															<CustomButton
																id="save-disabled"
																disabled={true}
																text="Save"
																onClickCallback={onSaveClick}
																data-testid={`${testid}-save-disabled`}
															/>
														)}
														{detail1 && category1 && (
															<CustomButton
																id="save"
																text="Save"
																onClickCallback={onSaveClick}
																data-testid={`${testid}-save`}
															/>
														)}
														<CustomButton
															id="discard"
															text="Discard"
															onClickCallback={() => onDiscardClick(row.productTypeId)}
															data-testid={`${testid}-discard`}
														/>
													</div>
												</TableCell>
												<TableCell />
												<TableCell />
												<TableCell />
												<TableCell />
											</>
										) : (
											<>
												<TableCell>{row.productTypeId}</TableCell>
												<TableCell>{row.productTypeName}</TableCell>
												<TableCell>
													{row.cuProductDetail?.map((p) => (
														<>
															{p.replace('__', ' - ')}
															<br />
														</>
													))}
												</TableCell>
												<TableCell>
													{row.cuProductCategory?.map((p) => (
														<>
															{p.replace('__', ' - ')}
															<br />
														</>
													))}
												</TableCell>
												<TableCell>{row.updatedDate ? dayjs(row.updatedDate).format('DD/MM/YYYY') : ''}</TableCell>
												<TableCell>{row.comments}</TableCell>
												{isAdmin && (
													<TableCell align="right">
														{!row.cuProductDetail || row.cuProductDetail.length === 0 ? (
															<ToolTip
																tooltipText="Add mapping"
																children={
																	<span>
																		<IconButton
																			id="add-mapping"
																			className={classes.iconButton}
																			disabled={disableActions}
																			onClick={() => handleAddEditClick(row, false)}
																		>
																			<AddIcon
																				fontSize="inherit"
																				sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }}
																			/>
																		</IconButton>
																	</span>
																}
															/>
														) : (
															<ToolTip
																tooltipText="Edit mapping"
																children={
																	<span>
																		<IconButton
																			id="edit-mapping"
																			className={classes.iconButton}
																			disabled={disableActions}
																			onClick={() => handleAddEditClick(row, true)}
																		>
																			<EditOutlinedIcon
																				sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }}
																			/>
																		</IconButton>
																	</span>
																}
															/>
														)}
													</TableCell>
												)}
											</>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						data-testid={`${testid}-pagination`}
						className={classes.pagination}
						rowsPerPageOptions={[25, 50, 100]}
						rowsPerPage={rowsPerPage}
						count={productTypeData.length}
						page={page - 1}
						onPageChange={() => {}}
						onRowsPerPageChange={(e) => handleRowPerPageChange(e.target.value)}
						ActionsComponent={() => (
							<Pagination
								data-testid={`${testid}-node`}
								count={productTypeData.length % rowsPerPage === 0 ? pageCount : pageCount + 1}
								page={page}
								onChange={handlePageChange}
								shape="rounded"
							/>
						)}
					/>
				</>
			)}
		</>
	);
};

const useStyles = makeStyles(() => ({
	filters: {
		paddingTop: '10px',
		'& #search': {
			backgroundColor: color.primary[100],
			color: color.text.light,
		},
	},
	table: {
		height: 'calc(100% - 205px)',
		'& .MuiTableCell-head': {
			...themes.bodyMedium,
			color: color.neutral[8],
			backgroundColor: color.neutral[1],
			borderTop: `1px solid ${color.neutral[2]}`,
		},
		'& .MuiTableCell-body': {
			...themes.bodyRegular,
			color: color.text.dark,
			padding: '3px 8px !important',
		},
		'& .MuiTableCell-root': {
			borderBottom: `1px solid ${color.neutral[2]}`,
			padding: '13px 8px',
		},
		'& .MuiTableRow-hover': {
			'&:hover': {
				backgroundColor: `${color.signal.infoLight} !important`,
			},
		},
	},
	pagination: {
		'& .MuiToolbar-root.MuiTablePagination-toolbar': {
			position: 'absolute',
			right: 20,
			paddingTop: '5px',
		},
		'& .MuiButtonBase-root.Mui-selected': {
			backgroundColor: color.primary[100],
			color: color.shades.white,
		},
		'& .MuiButtonBase-root.Mui-selected:hover': {
			backgroundColor: color.primary[100],
		},
		'& .MuiPagination-root.MuiPagination-text': {
			flexShrink: 0,
			marginLeft: '10px',
		},
	},
	iconButton: {
		padding: '5px !important',
		'& svg': {
			width: '16px',
			height: '16px',
		},
	},
	label: {
		position: 'absolute',
		right: '30px',
		paddingTop: '8px',
		'& .MuiTypography-root': {
			...themes.bodyRegular,
		},
	},
	modifyRow: {
		borderLeft: `5px solid ${color.signal.info}`,
	},
	actions: {
		'& #discard': {
			backgroundColor: color.primary[5],
			color: color.text.dark,
			border: 'none',
		},
		'& #save-disabled': {
			backgroundColor: color.neutral[6],
			color: color.text.light,
			border: 'none',
		},
		'& #save': {
			backgroundColor: color.primary[100],
			color: color.text.light,
			border: 'none',
		},
	},
	divComp: {
		display: 'flex',
		alignItems: 'center',
	},
	alert: {
		position: 'fixed',
		top: '10px',
		left: '50%',
		transform: 'translateX(-50%)',
	},
}));
